import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    Input,
    Signal,
    signal,
    ViewEncapsulation,
    OnInit,
} from '@angular/core';
import { FiOnboardingHomePageService } from '@fi-app/fi-onboarding/fi-onboarding-home-page/fi-onboarding-home-page.service';
import { IOnboardingCompletionData } from '@fi-app/fi-onboarding/fi-onboarding-completion/fi-onboarding-completion';
import { FiOnboardingCompletionService } from '@fi-app/fi-onboarding/fi-onboarding-completion/fi-onboarding-completion.service';
import { Animations } from '@fi-shared/utils/animations';
import { IOnboardingActive, IOnboardingActiveData } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding';
import { isNullOrUndefined } from '@fi-app/shared/utils/is-null-or-undefined';
import { FiOnboardingCompletionSyncService } from '@fi-app/fi-onboarding/fi-onboarding-completion/fi-onboarding-completion-sync.service';

@Component({
    selector: 'fi-onboarding-home-page',
    templateUrl: './fi-onboarding-home-page.component.html',
    styleUrl: './fi-onboarding-home-page.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-onboarding-home-page', '[attr.hidden]': 'isHidden()' },
    standalone: false,
    animations: [Animations.fadeHeight()],
})
export class FiOnboardingHomePageComponent implements OnInit {
    @Input({ required: true }) layoutId: string;
    @Input({ required: true }) placeholderIndex: number;

    private fiOnboardingHomePageService = inject(FiOnboardingHomePageService);
    private fiOnboardingCompletionService = inject(FiOnboardingCompletionService);
    private fiOnboardingCompletionSyncService = inject(FiOnboardingCompletionSyncService);

    protected isShownAtPlaceholder = signal(false);
    protected isCompletionDataLoading = signal(false);

    protected onboardingActiveData: Signal<IOnboardingActiveData> =
        this.fiOnboardingHomePageService.onboardingActiveData;
    protected visibleOnboardings: Signal<IOnboardingActive[]> = this.fiOnboardingHomePageService.visibleOnboardings;
    protected isSignleOnboardingExpanded = signal(false);

    protected isMultipleOnboardings = computed(() => this.visibleOnboardings().length > 1);

    private _currentDisaplyedOnboardingCompletion = signal<IOnboardingCompletionData>(null);
    protected readonly currentDisaplyedOnboardingCompletion = this._currentDisaplyedOnboardingCompletion.asReadonly();

    private isAllOnboardingsHidden = computed(() => this.visibleOnboardings().length === 0);
    protected isHidden = computed(() => {
        return !this.isShownAtPlaceholder() || this.isAllOnboardingsHidden() ? 'hidden' : undefined;
    });

    private _onboardingHomePageDataChangedEffect = effect(() => {
        const onboardingHomePageData = this.onboardingActiveData();
        if (isNullOrUndefined(onboardingHomePageData)) {
            return;
        }
        const isCurrentDisplayedOnboardingCompletionVisible = this.visibleOnboardings().find(
            (onboarding) => onboarding.Id === this.currentDisaplyedOnboardingCompletion()?.OnboardingId,
        )?.Id;
        if (!isCurrentDisplayedOnboardingCompletionVisible) {
            this.resetCurrentDisplayedOnboardingCompletion();
        }
        this.setIsShownAtPlaceholder();
    });

    public ngOnInit(): void {
        const currentOpenedCompletionStepData =
            this.fiOnboardingCompletionSyncService.currentOpenedCompletionStepData();
        if (!currentOpenedCompletionStepData?.isHomePage) {
            this.fiOnboardingCompletionSyncService.setCurrentOpenedCompletionStepData(undefined);
        } else {
            this.loadCompletionData(currentOpenedCompletionStepData?.onboardingId);
            this.isSignleOnboardingExpanded.set(true);
        }
    }

    private setIsShownAtPlaceholder(): void {
        // determine if we need to show active onboardings on main or widgets feed.
        this.isShownAtPlaceholder.set(
            this.fiOnboardingHomePageService.isShownAtPlaceholder({
                layoutId: this.layoutId,
                placeholderIndex: this.placeholderIndex,
                isHiddenFromHomePage: this.isAllOnboardingsHidden(),
                isAtSideWidgets: this.fiOnboardingHomePageService.isAtSideWidgets(),
            }),
        );
    }

    protected resetCurrentDisplayedOnboardingCompletion(): void {
        this._currentDisaplyedOnboardingCompletion.set(null);
    }

    protected loadCompletionData(onboardingId: number): void {
        // we should load completions only when user expands card for performance purposes
        this.isCompletionDataLoading.set(true);
        this.fiOnboardingCompletionService
            .getOnboardingCompletionData(onboardingId)
            .then((completionData) => {
                this._currentDisaplyedOnboardingCompletion.set(completionData);
            })
            .finally(() => {
                this.isCompletionDataLoading.set(false);
            });
    }
}
